import * as _ from 'underscore';
import { RefreshContentScope } from '../../app/ActionPlaceScopes';
import { SelectedItemsScope } from '../controllers/ContentSubplaceCtrl';
import { ISiteUsersPlaceScope } from '../controllers/SiteUsersPlaceCtrl';
import { FeatureFlags } from '../FeatureFlags';
import { IActionContext } from '../helpers/ActionContext';
import { CommonErrorCode } from '../helpers/ErrorCodes';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { IResult } from '../ServerApi/types/IResponse';
import { SiteRoles } from '../ServerApi/types/ISiteRole';
import { ISiteUser } from '../ServerApi/types/ISiteUser';
import { ActionHelpers } from './ActionHelpers';
import { IActionButton } from './ActionPanelService';
import { EditGroupMembershipAction } from './EditGroupMembershipAction';
import { ForcePasswordUpdateAction } from './ForcePasswordUpdateAction';
import { RemoveUsersFromSiteAction } from './RemoveUsersFromSiteAction';
import { ResetLoginRateLimitingAction } from './ResetLoginRateLimitingAction';
import { ServerService } from './ServerService';
import { SetSiteUsersSiteRoleAction } from './SetSiteUsersSiteRoleAction';
import { UpdateUserAuthenticationAction } from './UpdateUserAuthenticationAction';
import { WindowLocationService } from './WindowLocationService';

export class SiteUsersActions {

  private siteUsersActionDefinition: IActionButton<ISiteUser, {}>[];

  public static $inject = [
    'ServerService',
    'WindowLocationService',
    'EditGroupMembershipAction',
    'RemoveUsersFromSiteAction',
    'SetSiteUsersSiteRoleAction',
    'ForcePasswordUpdateAction',
    'ResetLoginRateLimitingAction',
    'UpdateUserAuthenticationAction'
  ];

  constructor(
    private serverService: ServerService,
    private WindowLocationService: WindowLocationService,
    private EditGroupMembershipAction: EditGroupMembershipAction,
    private RemoveUsersFromSiteAction: RemoveUsersFromSiteAction,
    private SetSiteUsersSiteRoleAction: SetSiteUsersSiteRoleAction,
    private ForcePasswordUpdateAction: ForcePasswordUpdateAction,
    private ResetLoginRateLimitingAction: ResetLoginRateLimitingAction,
    private UpdateUserAuthenticationAction: UpdateUserAuthenticationAction
  ) {

    this.siteUsersActionDefinition = [
      this.editUsersGroupMembershipAction(),
      this.setSiteUsersSiteRoleAction(),
      this.updateUserAuthenticationAction(),
      this.removeUsersFromSiteAction()
    ];
    if (FeatureFlags.localAuthPasswordExpirationEnabled && serverService.isServerDomainFamilyLocal()) {
      this.siteUsersActionDefinition.push(this.forcePasswordUpdateAction());
    }
    if (FeatureFlags.localAuthLoginRateLimitingEnabled && serverService.isServerDomainFamilyLocal()) {
      this.siteUsersActionDefinition.push(this.resetLoginRateLimitingAction());
    }
  }

  // Public API

  public getSiteUsersActionsButtons(): IActionButton<ISiteUser, {}>[] {
    return this.siteUsersActionDefinition;
  }

  // action button factory

  public editUsersGroupMembershipAction(): IActionButton<ISiteUser, {}> {
    return {
      name: 'groupMembership',
      translate: 'groupMembership_action',
      isAvailable: (items: Set<ISiteUser>): boolean => items.size() > 0,
      execute: (items: Set<any>, context: IActionContext) => {
        const scope = <RefreshContentScope & SelectedItemsScope<any>>context.scope;
        this.EditGroupMembershipAction.showEditGroupMembershipDialog(items, context)
          .then((result: IResult) => {
            if (result) {
              ActionHelpers.deselectNotFound(scope.selectedItems, result, CommonErrorCode.SiteUserNotFound);
              scope.refreshContent();
            }
          });
      }
    };
  }

  private removeUsersFromSiteAction(): IActionButton<ISiteUser, {}> {
    return {
      name: 'remove',
      translate: 'remove_action',
      isAvailable: (items: Set<ISiteUser>): boolean => this.canRemove(items),
      execute: (items: Set<any>, context: IActionContext) => {
        const scope = <ISiteUsersPlaceScope>context.scope;
        this.RemoveUsersFromSiteAction.showRemoveUsersFromSiteDialog(items, context)
          .then((result: IResult) => {
            if (result) {
              ActionHelpers.deselectAllExceptErrors(scope.selectedItems, result, CommonErrorCode.SiteUserNotFound);
              scope.refreshContent();
            }
          });
      }
    };
  }

    private updateUserAuthenticationAction(): IActionButton<ISiteUser, {}> {
      return {
        name: 'authentication',
        translate: 'authentication_action',
        isAvailable: (items: Set<ISiteUser>): boolean => this.canEditAuthenticationSetting(items),
        execute: (items: Set<any>, context: IActionContext) => {
          const scope = <ISiteUsersPlaceScope>context.scope;
          this.UpdateUserAuthenticationAction.showUpdateUserAuthenticationDialog(items, context)
            .then((result: IResult) => {
              ActionHelpers.deselectNotFound(scope.selectedItems, result, CommonErrorCode.SiteUserNotFound);
              scope.refreshContent();
              this.serverService.refreshSessionInfo();
            });
        }
      };
    }

  private canEditAuthenticationSetting(items: Set<ISiteUser>): boolean {
    return !items.isEmpty() && !SiteUsersActions.isSingleSelectAndGuest(items) && this.serverService.isSiteSamlEnabled();
  }

  // $note-ltan-1-28-2015
  // Used by SiteUsersPlace and GroupUsersSubplace
  public setSiteUsersSiteRoleAction(): IActionButton<ISiteUser, {}> {
    return {
      name: 'siteRole',
      translate: 'siteRole_action',
      isAvailable: (items: Set<ISiteUser>): boolean => this.canChangeRole(items),
      execute: (items: Set<any>, context: IActionContext) => {
        const scope = <RefreshContentScope & SelectedItemsScope<any>>context.scope;
        this.SetSiteUsersSiteRoleAction.showSetSiteUsersSiteRoleDialog(items, context)
          .then((result: IResult) => {
            ActionHelpers.deselectNotFound(scope.selectedItems, result, CommonErrorCode.SiteUserNotFound);
            scope.refreshContent();
          });
      }
    };
  }

  private forcePasswordUpdateAction(): IActionButton<ISiteUser, {}> {
    return {
      name: 'forcePasswordUpdate',
      translate: 'forcePasswordUpdate_action',
      isAvailable: (items: Set<ISiteUser>): boolean => this.isSelectingSomethingOtherThanGuest(items),
      execute: (items: Set<any>, context: IActionContext) => {
        const scope = <ISiteUsersPlaceScope>context.scope;
        this.ForcePasswordUpdateAction.onSiteUsers(context, items)
          .then((result: IResult) => {
            if (result) {
              ActionHelpers.deselectAllExceptErrors(scope.selectedItems, result, CommonErrorCode.SystemUserNotFound);
              scope.refreshContent();
            }
          });
      }
    };
  }

  private resetLoginRateLimitingAction(): IActionButton<ISiteUser, {}> {
    return {
      name: 'resetLoginRateLimiting',
      translate: 'resetLoginRateLimiting_action',
      isAvailable: (items: Set<ISiteUser>): boolean => this.isSelectingSomethingOtherThanGuest(items),
      execute: (items: Set<any>, context: IActionContext) => {
        const scope = <ISiteUsersPlaceScope>context.scope;
        this.ResetLoginRateLimitingAction.onSiteUsers(context, items)
          .then((result: IResult) => {
            if (result) {
              ActionHelpers.deselectAllExceptErrors(scope.selectedItems, result, CommonErrorCode.SystemUserNotFound);
              scope.refreshContent();
            }
          });
      }
    };
  }

  public isSelectingSomethingOtherThanGuest(items: Set<ISiteUser>): boolean {
    return !items.isEmpty() && !SiteUsersActions.isSingleSelectAndGuest(items);
  }

  // Determine if change role action is allowed.
  // - Disallow if all selected users are server admins unless the current user is a Server Admin
  // - Disallow if the only selection is a guest user.
  // - Disallow site admins from attempting to change role of a set of all server admins
  // - Disallow if session user cannot manage users
  private canChangeRole(users: Set<ISiteUser>) {
    return ((users.size() > 0)
      && !SiteUsersActions.isSingleSelectAndGuest(users)
      && (!SiteUsersActions.areAllSelectedUsersServerAdmins(users) || this.serverService.isSessionServerAdmin()))
      && this.serverService.canSessionUserManageUsers();
  }

  // Determine if remove users action is allowed.
  // - Disallow if session user is not allowed (per server setting) to manage users.
  // - Disallow if the only selection is a guest user.
  // - Disallow site admins from attempting to remove a set of all server admins
  private canRemove(users: Set<ISiteUser>) {
    const canManageUsers = this.serverService.canSessionUserManageUsers();
    return ((users.size() > 0)
      && canManageUsers
      && !SiteUsersActions.isSingleSelectAndGuest(users)
      && (!SiteUsersActions.areAllSelectedUsersServerAdmins(users) || this.serverService.isSessionServerAdmin()));
  }

  private static isSingleSelectAndGuest(users: Set<ISiteUser>) {
    return (users.size() === 1 && users.first().siteRole === SiteRoles.Guest);
  }

  private static areAllSelectedUsersServerAdmins(users: Set<ISiteUser>): boolean {
    return _.every(users.toArray(), (user: ISiteUser) => {
      // return true if every user is server admin
      return (user.siteRole === SiteRoles.ServerAdministrator);
    });
  }

}

Module.instance().service('SiteUsersActions', SiteUsersActions);
